import { IApp } from "model/app";
import { ICampaignServer } from "model/campaigns";
import { getApps } from "redux/apps/selectors";
import { IRootState } from "redux/reducer";
import { createDeepEqualSelector } from "redux/utils";
import { createSelector } from "reselect";

export type ICampaignsObject = {
  [id: string]: ICampaignServer;
};

//campaigns collection in form of object
export const getActiveCampaignsObject = (state: IRootState) =>
  state.campaigns?.active?.data;

export const getArchivedCampaignsObject = (state: IRootState) =>
  state.campaigns?.archived?.data;

export const getAllCampaignsObject = createDeepEqualSelector(
  getActiveCampaignsObject,
  getArchivedCampaignsObject,
  (active: ICampaignsObject, archived: ICampaignsObject) => ({
    ...active,
    ...archived,
  })
);

export const getCampaignsObject = {
  active: getActiveCampaignsObject,
  archived: getArchivedCampaignsObject,
};

//campaigns collection in form of array
function campaignsObjectToArray(obj: ICampaignsObject): ICampaignServer[] {
  return Object.values(obj);
}

export const getActiveCampaigns = createDeepEqualSelector(
  getActiveCampaignsObject,
  (campaigns: ICampaignsObject) => campaignsObjectToArray(campaigns)
);

export const getArchivedCampaigns = createDeepEqualSelector(
  getArchivedCampaignsObject,
  (campaigns) => campaignsObjectToArray(campaigns)
);

export const getAllCampaigns = createDeepEqualSelector(
  getActiveCampaigns,
  getArchivedCampaigns,
  (active: ICampaignServer[], archived: ICampaignServer[]) =>
    [...active, ...archived] as ICampaignServer[]
);

export const getCampaignsList = {
  active: getActiveCampaigns,
  archived: getArchivedCampaigns,
};

export const getCampaignsLoadingState = {
  active: (state) => [
    state.campaigns.active.loadingState,
    state.campaigns.active.error,
  ],
  archived: (state) => [
    state.campaigns.archived.loadingState,
    state.campaigns.archived.error,
  ],
};

export const getCampaignById = (state, id) => {
  const campaigns = getAllCampaignsObject(state);
  return campaigns?.[id] ?? null;
};

export const getAppByCampaignId = (state, campaignId) => {
  const campaign = getCampaignById(state, campaignId);
  if (!campaign) return;
  const app = getApps(state)[campaign.bundleId];
  return app as IApp;
};

export const getSkan4CampaignsByBundleId = createSelector(
  [(state: IRootState, bundleId: string) => bundleId, getAllCampaigns],
  (bundleId: string, campaigns: ICampaignServer[]) => {
    const result: ICampaignServer[] = [];
    for (const camp of campaigns) {
      if (camp.bundleId === bundleId && camp.isSkanSupportedCampaign) {
        result.push(camp);
      }
    }
    return result;
  }
);

export const getSameAppSkan4Campaigns = createSelector(
  [
    (state: IRootState, campaignId: string) => campaignId,
    getCampaignById,
    getAllCampaigns,
  ],
  (
    campaignId: string,
    campaign: ICampaignServer,
    campaigns: ICampaignServer[]
  ) => {
    const result: ICampaignServer[] = [];
    if (!campaign?.bundleId) return result;

    for (const camp of campaigns) {
      if (camp.bundleId === campaign.bundleId && camp.isSkanSupportedCampaign) {
        result.push(camp);
      }
    }
    return result;
  }
);

export const getSameAppCampaigns = createSelector(
  [
    (state: IRootState, campaignId: string) => campaignId,
    getCampaignById,
    getAllCampaigns,
  ],
  (
    campaignId: string,
    campaign: ICampaignServer,
    campaigns: ICampaignServer[]
  ) => {
    const result: ICampaignServer[] = [];
    if (!campaign?.bundleId) return result;

    for (const camp of campaigns) {
      if (camp.bundleId === campaign.bundleId) {
        result.push(camp);
      }
    }
    return result;
  }
);
